import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PlantingsImportResult,
  PlantingsImportService,
  PlantingsImportSourceType
} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { Dialog } from 'primeng/dialog';
import { FileUploadComponent } from '@components/file-upload/file-upload.component';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { TextareaComponent } from '@components/textarea';
import { FormsModule } from '@angular/forms';
import {
  extractImportErrorMessages,
  MappedPolygonImportError,
  mapPolygonImportErrors
} from '@app/pages/electronic-field-map/pages/import-fields/utils/polygon-import-validation.util';

@Component({
  selector: 'app-import-field-modal',
  templateUrl: './import-field-modal.html',
  imports: [
    Dialog,
    TranslatePipe,
    FileUploadComponent,
    PrimaryButton,
    SecondaryButton,
    TextareaComponent,
    FormsModule
  ],
  styleUrls: ['./import-field-modal.scss']
})
export class ImportFieldModalComponent {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() importSuccess = new EventEmitter<void>();

  formData = {
    comment: ''
  };

  selectedImportFiles: File[] = [];
  isImporting = false;

  /** Unique user-facing error plaques shown inside the modal. */
  validationErrors: MappedPolygonImportError[] = [];
  /** Soft-warning summary (partially valid import). */
  validationSummaryKey: string | null = null;
  validationSummaryParams: Record<string, number> = {};
  fileFormatErrorKey: string | null = null;

  constructor(
    private messageService: MessageService,
    private translate: TranslateService,
    private plantingsImportService: PlantingsImportService
  ) {}

  onFileSelected(files: File[]): void {
    if (!files || files.length === 0) {
      return;
    }

    this.clearValidationFeedback();

    for (const file of files) {
      const fileName = (file.name || '').toLowerCase();

      if (
        !fileName.endsWith('.shp') &&
        !fileName.endsWith('.zip') &&
        !fileName.endsWith('.kml') &&
        !fileName.endsWith('.geojson') &&
        !fileName.endsWith('.json')
      ) {
        // Inline plaque: CustomMessageService swallows severity=error toasts.
        this.fileFormatErrorKey = 'Error_Unsupported_import_fields';
        continue;
      }

      const exists = this.selectedImportFiles.some(
        (f) => f.name === file.name && f.size === file.size
      );
      if (!exists) {
        this.selectedImportFiles.push(file);
      }
    }
  }

  closeImportModal(): void {
    this.visible = false;
    this.selectedImportFiles = [];
    this.isImporting = false;
    this.clearValidationFeedback();
    this.visibleChange.emit(this.visible);
  }

  performImport(): void {
    if (!this.selectedImportFiles?.length || this.isImporting) {
      return;
    }

    this.isImporting = true;
    this.clearValidationFeedback();

    // Staging into ImportedFieldReport (SourceType=ImportedField, stageOnly=true).
    this.plantingsImportService
      .cropMonitoringPlantingsImportImportPost(
        PlantingsImportSourceType.NUMBER_6,
        this.selectedImportFiles,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
        this.formData.comment
      )
      .subscribe({
        next: (result: PlantingsImportResult) => this.handleImportSuccess(result),
        error: (error: unknown) => this.handleImportError(error)
      });
  }

  onFileRemoved(event: any): void {
    this.fileFormatErrorKey = null;

    if (typeof event === 'number') {
      this.selectedImportFiles.splice(event, 1);
      return;
    }

    if (event?.name) {
      const idx = this.selectedImportFiles.findIndex(
        (f) => f.name === event.name && f.size === event.size
      );
      if (idx >= 0) {
        this.selectedImportFiles.splice(idx, 1);
      }
    }
  }

  private handleImportSuccess(result: PlantingsImportResult): void {
    this.isImporting = false;

    const invalidCount =
      result?.countNotValidImportedField ?? result?.polygonsInvalid ?? 0;
    const validCount = result?.countValidImportedField ?? 0;
    const mappedErrors = mapPolygonImportErrors(result?.errors ?? []);

    // Soft failure: some polygons rejected, but report was created.
    // Keep modal open so error plaques stay visible.
    if (invalidCount > 0 || mappedErrors.length > 0) {
      this.validationErrors = mappedErrors;
      this.validationSummaryKey = 'IMPORT_PARTIAL_POLYGON_ERRORS';
      this.validationSummaryParams = {
        invalid: invalidCount || mappedErrors.length,
        valid: validCount
      };

      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('WARNING'),
        detail: this.translate.instant('IMPORT_PARTIAL_POLYGON_ERRORS', {
          invalid: invalidCount || mappedErrors.length,
          valid: validCount
        })
      });

      this.importSuccess.emit();
      return;
    }

    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('SUCCESS'),
      detail: this.translate.instant('IMPORT_SUCCESS') || 'Import successful'
    });
    this.importSuccess.emit();
    this.closeImportModal();
  }

  private handleImportError(error: unknown): void {
    this.isImporting = false;

    const rawMessages = extractImportErrorMessages(error);
    this.validationErrors = mapPolygonImportErrors(rawMessages);

    if (this.validationErrors.length === 0) {
      this.validationErrors = [
        {
          code: 'unknown',
          messageKey: 'IMPORT_FAILED',
          rawMessage: ''
        }
      ];
    }

    this.validationSummaryKey = 'IMPORT_ALL_POLYGONS_INVALID';
    this.validationSummaryParams = {};
  }

  private clearValidationFeedback(): void {
    this.validationErrors = [];
    this.validationSummaryKey = null;
    this.validationSummaryParams = {};
    this.fileFormatErrorKey = null;
  }
}
